import { z } from 'zod';

import { dateTimeSchema } from '@apple/utils/globalization/models';

import { type OrderStatus, orderStatusSchema } from '@apple/features/order/models/status';
import {
	type ShopProductInventory,
	shopProductInventorySchema,
} from '@apple/features/product/models/listing';

export interface ShippingOption {
	id: number;
	displayName: string;
	price: string | null;
}

// TODO: Move this to a shared location and make it useable by channel too.
export const shippingOptionSchema = z.object({
	id: z.number(),
	displayName: z.string(),
	price: z.string().nullable(),
}) satisfies z.ZodType<ShippingOption>;

/**
 * @see AppleRetail.Web.Features.Orders.AppleRetailOrderDetailsController.OrderDetailHeader
 */
export interface OrderDetailResponse {
	purchaseOrderNumber: string;
	salesOrderNumber: string;
	orderedBy: number;
	orderedByEmailAddress: string;
	orderedByLastName: string;
	orderedByFirstName: string;
	status: string;
	orderDate: string | Date;
	dateApproved: string | Date | null;
	approvedBy: string | null;
	updatedDate: string | Date | null;
	shippingDate: string | Date | null;
	customerReferenceNumber: string;
	customerGroupName: string;
	storeName: string;
	vintage: string | null;
	initialOrderComment: string;
	bypassBudget: boolean;
	line1: string;
	line2: string;
	line3: string;
	city: string;
	state: string;
	postalCode: string;
	countryCode: string;
	countryDisplayName: string;
	logoImageUrl: string;
	thirdPartyCustomerId: string;
	customerCode: string;
	shippingType: string;
	availableShippingOptions: ShippingOption[];
	selectedShippingOptionId: number;
	selectedShippingOptionPrice: string;
	isOrderMarkedForCancellation: boolean;
	areAllLinesMarkedForCancellation: boolean;
	isOrderCancellable: boolean;
	holdForSubmissionUntilDate: string | Date | null;
	isOrderEditable: boolean;
	orderTotal: string;
	customerId: number;
	scheduledShipmentDays: string;
	referenceNumber?: string | null;
	deliverFrom: string | null;
	deliverBy: string | null;
	orderReason: string;
}

/**
 * @see AppleRetail.Web.Features.Orders.AppleRetailOrderLineItemsController
 */
export interface LineItemStatus {
	itemStatus: OrderStatus;
	status: string;
	internalStatus: number;
	quantity: number;
	trackingUrl: string | null;
	trackingNumber: string | null;
	carrierName: string | null;
	shipDate: string | null;
}
/**
 * @see AppleRetail.Web.Features.Orders.AppleRetailOrderLineItemsController
 */
export interface OrderLineItem {
	parentPosition: number | null;
	position: number;
	sku: string;
	title: string;
	quantity: number;
	status: string;
	unitsPerCarton: string;
	isInventoryItem: boolean;
	statusList: LineItemStatus[];
	approvalRequired: boolean;
	shippedQuantity: number;
	approvalRequiredText: string;
	quantityLimit: number;
	unitOfMeasure: string;
	unitAmount: number;
	formattedUnitAmount: string;
	totalAmount: string;
	imageUrl: string;
	externalLineItemKey: string;
	cancellationRequestedOn: string | Date | null;
	canRequestCancellation: boolean;
	approvalReasons: string[];
	inventory: ShopProductInventory | null;
	bypassBudget: boolean;
	canBypassBudget: boolean;
	showBudgetInfo: boolean;
	isInFulfillment: boolean;
	cancellationFailed: boolean;
	canReorder: boolean;
}

/**
 * @see AppleRetail.Web.Features.Orders.AppleRetailOrderLineItemsController
 */
export const lineItemStatusSchema = z.object({
	itemStatus: orderStatusSchema,
	status: z.string(),
	internalStatus: z.number(),
	quantity: z.number(),
	trackingUrl: z.string().nullable(),
	trackingNumber: z.string().nullable(),
	carrierName: z.string().nullable(),
	shipDate: z.string().nullable(),
});
/**
 * @see AppleRetail.Web.Features.Orders.AppleRetailOrderLineItemsController
 */
export const orderLineItemSchema = z.object({
	parentPosition: z.number().nullable(),
	position: z.number(),
	sku: z.string(),
	title: z.string(),
	quantity: z.number(),
	status: z.string(),
	unitsPerCarton: z.string(),
	isInventoryItem: z.boolean(),
	statusList: z.array(lineItemStatusSchema),
	approvalRequired: z.boolean(),
	shippedQuantity: z.number(),
	approvalRequiredText: z.string(),
	quantityLimit: z.number(),
	unitOfMeasure: z.string(),
	unitAmount: z.number(),
	formattedUnitAmount: z.string(),
	totalAmount: z.string(),
	imageUrl: z.string(),
	externalLineItemKey: z.string(),
	cancellationRequestedOn: dateTimeSchema.nullable(),
	canRequestCancellation: z.boolean(),
	approvalReasons: z.array(z.string()),
	inventory: shopProductInventorySchema.nullable(),
	bypassBudget: z.boolean(),
	canBypassBudget: z.boolean(),
	showBudgetInfo: z.boolean(),
	isInFulfillment: z.boolean(),
	cancellationFailed: z.boolean(),
	canReorder: z.boolean(),
}) satisfies z.ZodType<OrderLineItem>;

/**
 * @see AppleRetail.Web.Features.Orders.AppleRetailOrderDetailsController.OrderDetailHeader
 */
export const orderDetailResponseSchema = z.object({
	purchaseOrderNumber: z.string(),
	salesOrderNumber: z.string(),
	orderedBy: z.number(),
	orderedByEmailAddress: z.string(),
	orderedByLastName: z.string(),
	orderedByFirstName: z.string(),
	status: z.string(),
	orderDate: dateTimeSchema,
	dateApproved: dateTimeSchema.nullable(),
	approvedBy: z.string().nullable(),
	updatedDate: dateTimeSchema.nullable(),
	shippingDate: dateTimeSchema.nullable(),
	customerReferenceNumber: z.string(),
	customerGroupName: z.string(),
	storeName: z.string(),
	vintage: z.string().nullable(),
	initialOrderComment: z.string(),
	bypassBudget: z.boolean(),
	line1: z.string(),
	line2: z.string(),
	line3: z.string(),
	city: z.string(),
	state: z.string(),
	postalCode: z.string(),
	countryCode: z.string(),
	countryDisplayName: z.string(),
	logoImageUrl: z.string(),
	thirdPartyCustomerId: z.string(),
	customerCode: z.string(),
	shippingType: z.string(),
	availableShippingOptions: z.array(shippingOptionSchema),
	selectedShippingOptionId: z.number(),
	selectedShippingOptionPrice: z.string(),
	isOrderMarkedForCancellation: z.boolean(),
	areAllLinesMarkedForCancellation: z.boolean(),
	isOrderCancellable: z.boolean(),
	holdForSubmissionUntilDate: dateTimeSchema.nullable(),
	isOrderEditable: z.boolean(),
	orderTotal: z.string(),
	customerId: z.number(),
	scheduledShipmentDays: z.string(),
	referenceNumber: z.string().nullable(),
	deliverFrom: z.string().nullable(),
	deliverBy: z.string().nullable(),
	orderReason: z.string(),
}) satisfies z.ZodType<OrderDetailResponse>;
